import { gql } from 'apollo-boost';
import styled from 'styled-components';
import { sbEditable } from '@storyblok/storyblok-editable';
import { NODE_PARAGRAPH, render } from 'storyblok-rich-text-react-renderer';
import { useState } from 'react';

import Button from 'components/atoms/Button';
import { Modal } from 'antd';
import Colors from 'components/tokens/Colors';
import { Container, Row, Col } from 'components/atoms/Grid';
import Text from 'components/atoms/Text';
import LenteNewsletterSubscribeForm from 'components/newsletter';
import Svg from 'components/atoms/Svg';

import { useStoryblok } from 'hooks/useStoryblok';
import RouterLink from 'components/atoms/RouterLink';
import { useEditor } from 'providers/generalContext';

const query = gql`
    {
        GlobalItem(id: "global/footer") {
            content {
                _editable
                _uid
                component
                global
            }
        }
    }
`;

function Footer({ hidden }) {
    const [isVisibleModalCancel, setIsVisibleModalCancel] = useState(false);

    const { isStoryblokEditor } = useEditor();

    const { story, loading } = useStoryblok(query, 'GlobalItem');

    const content = story?.content?.global?.[0];

    function renderText(text) {
        return render(text, {
            nodeResolvers: {
                [NODE_PARAGRAPH]: children => {
                    return <LinkText>{children}</LinkText>;
                }
            }
        });
    }

    const showModal = () => {
      setIsVisibleModalCancel(true);
    }
    const handleClose = () => {
      setIsVisibleModalCancel(false);
    }
    const date = new Date();
    const currentYear = date.getFullYear();

    return (
        <>
            {!hidden && !loading && (
                <FooterStyled {...sbEditable(content)}>
                    <Container px="1.5 4">
                        <Row mt="5 8" mb="8 11">
                            <Col size="12">
                                <Row>
                                    <Col size="12 3" order="10 0">
                                        <Row>
                                            <LogosGrid>
                                                {content?.logos?.map((logo, index) => {
                                                    if (logo.link.cached_link === '/') {
                                                        return (
                                                            <RouterLink
                                                                to={logo.link.cached_url}
                                                                editor={isStoryblokEditor}
                                                                decoration="none"
                                                                key={index}
                                                            >
                                                                <LogoWrapper color={Colors.white}>
                                                                    {logo.image.filename.includes('svg') ? (
                                                                        <Svg url={logo.image.filename} />
                                                                    ) : (
                                                                        <img
                                                                            src={logo.image.filename}
                                                                            alt={logo.image.alt}
                                                                            style={{ maxWidth: '100px' }}
                                                                        />
                                                                    )}
                                                                </LogoWrapper>
                                                            </RouterLink>
                                                        );
                                                    }
                                                    return (
                                                        <a
                                                            href={logo.link.cached_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            key={index}
                                                        >
                                                            <LogoWrapper color={Colors.white}>
                                                                {logo.image.filename.includes('svg') ? (
                                                                    <Svg url={logo.image.filename} />
                                                                ) : (
                                                                    <img
                                                                        src={logo.image.filename}
                                                                        alt={logo.image.alt}
                                                                        style={{ maxWidth: '100px' }}
                                                                    />
                                                                )}
                                                            </LogoWrapper>
                                                        </a>
                                                    );
                                                })}
                                            </LogosGrid>
                                        </Row>
                                    </Col>
                                    <Col size="12 2" />
                                    <Col size="12 2" mb="3" order="1 0">
                                        <Row>
                                            <Col size="12" mb="2">
                                                <Text heading5 size="29" color={Colors.primary}>
                                                    {content?.first_column_title}
                                                </Text>
                                            </Col>

                                            {content?.first_column_links.map((item, i) => {
                                                if (item.link.linktype === 'url') {
                                                    return (
                                                        <Col
                                                            key={i}
                                                            size="12"
                                                            mb="1"
                                                            {...sbEditable(content.first_column_links[i])}
                                                        >
                                                            <a
                                                                href={item.link.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                <Text body2>{renderText(item.text)}</Text>
                                                            </a>
                                                        </Col>
                                                    );
                                                } else {
                                                    return (
                                                        <Col
                                                            key={i}
                                                            size="12"
                                                            mb="1"
                                                            {...sbEditable(content.first_column_links[i])}
                                                        >
                                                            <a href={`/${item.link.cached_url}`}>
                                                                <Text body2>{renderText(item.text)}</Text>
                                                            </a>
                                                        </Col>
                                                    );
                                                }
                                            })}
                                        </Row>
                                    </Col>
                                    <Col size="12 2" mb="1" order="3 0">
                                        <Row>
                                            <Col size="12" mb="2">
                                                <Text heading5 size="29" color={Colors.primary}>
                                                    {content?.second_column_title}
                                                </Text>
                                            </Col>

                                            {content?.second_column_links.map((item, i) => {
                                                if (item.link.linktype === 'url') {
                                                    return (
                                                        <Col
                                                            key={i}
                                                            size="12"
                                                            mb="1"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            {...sbEditable(content.second_column_links[i])}
                                                        >
                                                            <a href={item.link.url}>
                                                                <Text body2>{renderText(item.text)}</Text>
                                                            </a>
                                                        </Col>
                                                    );
                                                } else {
                                                    return (
                                                        <Col
                                                            key={i}
                                                            size="12"
                                                            mb="1"
                                                            {...sbEditable(content.second_column_links[i])}
                                                        >
                                                            <a href={`/${item.link.cached_url}`}>
                                                                <Text body2>{renderText(item.text)}</Text>
                                                            </a>
                                                        </Col>
                                                    );
                                                }
                                            })}
                                        </Row>
                                    </Col>
                                    <Col size="12 3" mb="2" order="5 0">
                                        <Row>
                                            <Col size="12" mb="2">
                                                <Text heading5 size="29" color={Colors.primary}>
                                                    {content?.third_column_title}
                                                </Text>
                                            </Col>

                                            {content?.third_column_links.map((item, i) => {
                                                if (item.link.linktype === 'url') {
                                                    return (
                                                        <Col
                                                            key={i}
                                                            size="12"
                                                            mb="1"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            {...sbEditable(content.third_column_links[i])}
                                                        >
                                                            <a
                                                                href={item.link.url}
                                                                {...sbEditable(content.third_column_links[i])}
                                                            >
                                                                <Text body2>{renderText(item.text)}</Text>
                                                            </a>
                                                        </Col>
                                                    );
                                                } else {
                                                    return (
                                                        <Col
                                                            key={i}
                                                            size="12"
                                                            mb="1"
                                                            {...sbEditable(content.third_column_links[i])}
                                                        >
                                                            <a href={`/${item.link.cached_url}`}>
                                                                <Text body2>{renderText(item.text)}</Text>
                                                            </a>
                                                        </Col>
                                                    );
                                                }
                                            })}
                                        </Row>
                                    </Col>
                                    <Col size="12 5" order="0" />
                                    {content?.last_line.map((item, i) => {
                                        if (item.link.linktype === 'url') {
                                            return (
                                                <Col
                                                    size="12 2"
                                                    order={`${i + 1 * 2} 0`}
                                                    mb="5"
                                                    key={i}
                                                    {...sbEditable(content.last_line[i])}
                                                >
                                                    <a href={item.link.url} target="_blank" rel="noopener noreferrer">
                                                        <Text body2>{renderText(item.text)}</Text>
                                                    </a>
                                                </Col>
                                            );
                                        } else {
                                            return (
                                                <Col
                                                    size="12 2"
                                                    order={`${i + 1 * 2} 0`}
                                                    mb="5"
                                                    key={i}
                                                    {...sbEditable(content.last_line[i])}
                                                >
                                                    <a href={`/${item.link.cached_url}`}>
                                                        <Text body2>{renderText(item.text)}</Text>
                                                    </a>
                                                </Col>
                                            );
                                        }
                                    })}

                                    <Col size="12" order="7 0" mb=".3">
                                        <Text heading5 color={Colors.primary}>
                                            {content?.title}
                                        </Text>
                                    </Col>
                                    <Col size="12" order="7 0" mb="2">
                                        <Text heading5 color={Colors.primary}>
                                            {content?.title_2}
                                        </Text>
                                    </Col>
                                    <Col size="12 4" order="7 0" mb="7">
                                     <Button
                                        style={{ width: '200px', color: 'black', padding: '8px 4px' }}
                                        primary
                                        onClick={showModal}
                                     >
                                       Inscreva-se
                                     </Button>
                                     <Modal
                                        open={isVisibleModalCancel}
                                        onCancel={handleClose}
                                        footer={null}
                                        centered
                                     >
                                        <LenteNewsletterSubscribeForm />
                                    </Modal>
                                    </Col>
                                    <Col size="12 3" />
                                    <Col size="12 3" order="8 0" mb="2 6">
                                        <Text heading5 size="31" color={Colors.primary}>
                                            Acompanhe nossas redes
                                        </Text>
                                    </Col>
                                    <Col size="6 2" order="8 0" mb="6" pl="1.3 1">
                                        <Row justifyContent="space-around" alignItems="center">
                                            {content?.footer_social_media.map((item, i) => {
                                                if (item.icon?.filename) {
                                                    const svgUrl = item.icon.filename.replace(
                                                        'https://a.storyblok.com',
                                                        'https://s3.amazonaws.com/a.storyblok.com'
                                                    );
                                                    return (
                                                        <a
                                                            key={i}
                                                            href={item.link.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            {...sbEditable(content.footer_social_media[i])}
                                                        >
                                                            <IconWrapper color={Colors.white}>
                                                                <Svg url={svgUrl} />
                                                            </IconWrapper>
                                                        </a>
                                                    );
                                                }
                                                return null;
                                            })}
                                        </Row>
                                    </Col>
                                </Row>

                                <Row justifyContent="flex-start space-between" alignItems="center">
                                    <Col size="12 9" mt="2 0" text="left">
                                        <Text color={Colors.primary}>
                                            Lupa © {currentYear} Todos os direitos reservados
                                        </Text>
                                    </Col>
                                    <Col size="12 3" text="left right" mt="5 0">
                                        <Row alignItems="center">
                                            <Col size="3 7" text="left right" pr="0 0.5">
                                                <Text size="12 16" lineHeight="16 21.79">
                                                    Feito por
                                                </Text>
                                            </Col>
                                            <Col size="2 3" text="left right" pl="0.5 1">
                                                <a
                                                    href="https://www.dex01.com.br/main/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label="Site da Dex01"
                                                >
                                                    <img
                                                        src={require('./assets/Logo_DEx01.png').default}
                                                        alt="Dex01"
                                                        width={52}
                                                        height={33}
                                                    />
                                                </a>
                                            </Col>
                                            <Col size="2" pl="1 0">
                                                <a
                                                    href="https://meza.digital/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    aria-label="Site da Meza Digitak"
                                                >
                                                    <img
                                                        src={require('./assets/Logo_Meza.png').default}
                                                        alt="Meza Digital"
                                                        width={32}
                                                        height={33}
                                                    />
                                                </a>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Container>
                </FooterStyled>
            )}
        </>
    );
}

const FooterStyled = styled.div`
    background-color: ${Colors.black};
    color: ${Colors.white};

    a {
        text-decoration: none;
        color: inherit;

        &:hover {
            text-decoration: underline;
        }
    }
`;

const IconWrapper = styled.div`
    display: flex;
    align-items: center;
    .svgInline {
        svg {
            max-width: 100px;

            path {
                fill: ${props => props.color} !important;
            }
        }
    }
`;

const LinkText = styled.p`
    margin: 0 0 8px;

    font-size: 1.6rem;
    line-height: 2.179rem;
`;

const LogosGrid = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    gap: 2rem;

    @media screen and (max-width: 819px) {
        width: 100%;
        flex-direction: row;
    }

    @media (max-width: 580px) {
        flex-wrap: wrap;
        justify-content: flex-start;
        padding: 0 30px;
    }
`;

const LogoWrapper = styled.div`
    display: flex;
    align-items: center;

    .svgInline {
        svg {
            max-width: 100px;
            width: 100%;
            path {
                width: 100%;
            }
        }
    }
`;

export default Footer;
